<template>
    <slot></slot>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';

import { useActions, useGetters } from '~coreModules/core/js/composables/vuexHelpers';
import { useLogger } from '~coreModules/core/js/composables/useLogger';

import { NOTIFICATION_VIEWED } from '~coreModules/core/js/global-event-constants';
import { NOTIFICATION_TYPES } from '~coreModules/analytics/js/analytics-constants';

import { GLOBAL_EVENT } from '~coreModules/core/js/store';
import {
    NOTIFICATIONS_MODULE_NAME,
    SET_NOTIFICATION_VIEWED,
} from '~coreModules/notifications/js/notifications-store';
import { MODALS_MODULE_NAME, LAUNCH_MODAL } from '~coreModules/modals/js/modals-store';

import { isNotificationPageBlocked } from '~coreModules/notifications/js/notifications-utils';

const props = defineProps({
    allowNotifications: {
        type: Boolean,
        default: false,
    },
    modalHashEvents: {
        type: Object,
        required: true,
    },
});

const route = useRoute();
const logger = useLogger();

const { activeAnonymousModalNotification } = useGetters(NOTIFICATIONS_MODULE_NAME, [
    'activeAnonymousModalNotification',
]);

const { activeModalId } = useGetters(MODALS_MODULE_NAME, [
    'activeModalId',
]);

const { setNotificationViewed } = useActions(NOTIFICATIONS_MODULE_NAME, {
    setNotificationViewed: SET_NOTIFICATION_VIEWED,
});

const { launchModal } = useActions(MODALS_MODULE_NAME, {
    launchModal: LAUNCH_MODAL,
});

const { trackGlobalEvent } = useActions({
    trackGlobalEvent: GLOBAL_EVENT,
});

const showNotificationTimeout = ref(null);
const activeNotification = ref(null);

const activeModalNotification = computed(() => activeAnonymousModalNotification(route.fullPath));

function handleNotificationViewed(notification, shouldTrackView = true) {
    if (shouldTrackView) {
        trackGlobalEvent({
            type: NOTIFICATION_VIEWED,
            data: {
                notificationType: NOTIFICATION_TYPES.modal,
                notificationSlug: notification?.notificationId,
            },
        });
    }

    setNotificationViewed({
        ...notification,
        shouldTrackView,
        clearNotifications: shouldTrackView,
    });

    activeNotification.value = null;
}

function stopNotification(notification) {
    clearTimeout(showNotificationTimeout.value);
    handleNotificationViewed(notification, false);
}

function showNotification(notification) {
    const { modalId } = notification;
    if (!props.allowNotifications) {
        logger.debugError(`attempted to launch a modal via nuuly notifications, but the user
            is not eligible to view notifications, cancelling modal launch for slug: ${modalId}`);
        handleNotificationViewed(notification, false);
        return;
    }

    const modalHashEvent = props.modalHashEvents[`#${modalId}`];

    if (modalHashEvent) {
        if (activeModalId.value) {
            logger.debugError(`attempted to launch a modal via nuuly notifications,
                but the user was already viewing the ${activeModalId.value} modal,
                        cancelling modal launch`);
            handleNotificationViewed(notification, false);
        } else {
            launchModal(modalHashEvent)
                .catch((error) => {
                    logger.debugError('launch of nuuly modal failed,\n', error);
                    handleNotificationViewed(notification, false);
                });
        }
    } else {
        logger.debugError(`attempted to launch a modal via nuuly notifications, but no
            modalHashEvent was found for slug: ${modalId}`);
        handleNotificationViewed(notification, false);
    }
}

watch(activeModalNotification, (newNotification, oldNotification) => {
    if (newNotification && oldNotification !== newNotification) {
        activeNotification.value = newNotification;

        const { delay } = newNotification;

        showNotificationTimeout.value = setTimeout(() => {
            showNotification(newNotification);
        }, delay);
    }
}, { immediate: true });

watch(activeModalId, (newModalId) => {
    if (!!newModalId && newModalId === activeNotification.value?.modalId) {
        handleNotificationViewed(activeNotification.value);
    }
});

watch(() => route.fullPath, (routePath) => {
    if (activeNotification.value && isNotificationPageBlocked(routePath, activeNotification.value)) {
        stopNotification(activeNotification.value);
    }
});
</script>
